import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { KeyRound, Power, ChevronRight, ChevronLeft, ChevronDown, BookOpen, MessageSquare, Loader2, Plus, Search, X, Pencil, Trash2, GraduationCap } from "lucide-react";

import { authFetch } from "../utils/auth";
import AllChatsModal from "./AllChatsModal";
import { useAppVersion } from "./CacheBuster";
import { NEW_SESSION_PATH, ENT_BASE_PATH, entSessionPath } from "./dashboard5/sessionRoutes";

// Dispatched by the copilot dashboard when a turn has been persisted, so the
// sidebar can pull the new session into Recents without a page reload. A window
// event rather than a prop: SideBar is rendered by several pages and none of
// them own the socket.
//
// Declared BELOW every import: CRA's eslint runs `import/first`, and a
// statement wedged between imports fails the whole build.
export const RECENTS_REFRESH_EVENT = "arta-recents-refresh";

// Dispatched with { detail: { sessionId, title } } the moment the first message
// of a new chat is sent, so the session appears in Recents immediately instead
// of when the server finishes committing the turn. The row is provisional: the
// next real fetch replaces it, matching on session id. Same "below every
// import" rule as above.
export const RECENTS_OPTIMISTIC_EVENT = "arta-recents-optimistic";

// Dispatched with { detail: { sessionId } } when a session is retired — a
// `/clear` in the composer, or the server's feedback prompt on a natural
// close — so the rail marks the row Closed immediately instead of waiting on
// the `/api/copilot/sessions` fetch, which reads Postgres and lags the
// write-behind outbox that would otherwise flush `closed` seconds late.
export const RECENTS_CLOSED_EVENT = "arta-recents-closed";

// Recents rows are one line; anything longer is the middle of a paragraph the
// user typed and reads as noise in a 240px rail.
// Clicking a session remounts this component: App.js keys TseDashboard5 on the
// route's session id and TseDashboard5 renders the sidebar, so a soft nav
// between sessions throws the fetched list away and refetches from empty. That
// is what put a spinner over Recents on every click. Holding the last list at
// module scope makes the remount a no-op visually — the rows are already there,
// and the refetch quietly replaces them.
//
// Owner-tagged so a re-login in the same tab (no reload) can never show the
// previous account's sessions for the ~700ms before the fetch returns.
let recentsCache = [];
let optimisticCache = [];
let recentsCacheOwner = null;

// Keyed on the surface as well as the account: the two rails hold DIFFERENT
// lists for the same user, and a cache tagged by username alone would seed the
// enterprise rail with the copilot's rows (or the reverse) for the ~700ms
// before its own fetch returns.
const currentOwner = (surface = "") => {
  try { return `${localStorage.getItem("username") || ""}|${surface}`; } catch { return `|${surface}`; }
};

// How many rows the rail itself draws. The endpoint is asked for one more than
// this so "is there a 31st?" is answerable without a second request — the extra
// row is never rendered, it only decides whether "View all chats" appears.
const RECENTS_VISIBLE_LIMIT = 30;
const RECENTS_FETCH_LIMIT = RECENTS_VISIBLE_LIMIT + 1;

// How long an optimistic row is allowed to show "Saving…" before the label
// falls back to its normal subtitle. Set just past the last retry in
// PENDING_RETRY_DELAYS_MS (12000ms below) so the label only gives up once the
// bounded catch-up has genuinely finished chasing the row — not a second
// sooner. This only ever changes the LABEL: the row stays `pending` (see the
// comment at the optimistic row's `pending: true` for why that flag must not
// move), so rename/delete remain hidden until the real fetch confirms it.
const SAVING_LABEL_TIMEOUT_MS = 15000;

// Newest first, on the server's own updated_at rather than the formatted "20h
// ago" string next to it. The list arrives ordered, but optimistic rows are
// spliced in at the front and a rename or a resumed chat moves a row's
// timestamp — so the order is re-established here rather than assumed.
const byUpdatedDesc = (a, b) => {
  // An optimistic row has no timestamp yet: it was created seconds ago, so it
  // sorts above everything rather than falling to the bottom as a zero.
  const ta = a.updatedAt ? new Date(a.updatedAt).getTime() : Infinity;
  const tb = b.updatedAt ? new Date(b.updatedAt).getTime() : Infinity;
  if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
  if (Number.isNaN(ta)) return 1;
  if (Number.isNaN(tb)) return -1;
  return tb - ta;
};

const OPTIMISTIC_TITLE_MAX = 60;
const truncateTitle = (text) => {
  const t = String(text || "").replace(/\s+/g, " ").trim();
  if (!t) return "New chat";
  return t.length > OPTIMISTIC_TITLE_MAX ? `${t.slice(0, OPTIMISTIC_TITLE_MAX - 1)}…` : t;
};

// `batch` turns the rail's list section into the batch-case list instead of
// copilot Recents. It is the ONLY source of batch data: BatchMode renders this
// sidebar itself and already owns every bit of that state (fetch, search,
// filter, delete), so the rail is a pure view over props — no fetch, no window
// events, nothing to keep in sync. Absent (null) on every other page, where the
// rail behaves exactly as it always has.
// Shape: { cases, filteredCases, loading, activeCaseId, search, onSearchChange,
//          statusFilter, onStatusFilterChange, onSelectCase, onDeleteCase,
//          onDeleteAll, deleting }
// `filteredCases` arrives already searched and status-filtered; `cases` is the
// unfiltered set and is only used for the "6/70" count and the empty states.
const SideBar = ({ mode = "user", onLogout, isDarkMode = false, onToggleTheme, onLinkClick, currentPath, onExpandedChange, hideActions = false, batch = null, showRecents = false, onNewChat, hideRecents = false }) => {
  const navigate = useNavigate();
  // The enterprise rail: user mode, but with the copilot's chat history in it.
  // Opted into by the page rather than inferred from the role, because the
  // plain user chat renders the same `mode="user"` sidebar and has no sessions
  // of its own to list.
  const entRecents = mode !== "agent" && !!showRecents;
  // Where a clicked row goes, and where a deleted-out-from-under-you session
  // falls back to. /TseDashboard5 is gated on AGENT_ROLES, so enterprise has
  // its own base (see sessionRoutes).
  const sessionPathFor = (id) => (entRecents ? entSessionPath(id) : `/TseDashboard5/${id}`);
  // Rail preferences are per-rail. The enterprise rail must never read or write
  // the copilot's keys: a support engineer who works in both would otherwise
  // carry one side's collapsed rail into the other.
  const railPrefix = mode === "agent" ? "agentSideBar" : "entSideBar";
  // Which surface's chats this rail lists. An account that has used both UIs
  // owns both sets under one user_id, so without this an enterprise user is
  // shown their copilot history too (and the copilot would show theirs).
  // Empty for the agent rail: it asks for the unscoped list exactly as before.
  const surfaceScope = entRecents ? "customer" : "";
  // Shown beside the logo when open, above Logout when collapsed — the rail is
  // the one chrome present on every page that has one, so it is where a support
  // question ("what build are you on?") gets answered.
  const appVersion = useAppVersion();
  // Which copilot session is open. `currentPath` is passed as a constant by the
  // dashboard ("/TseDashboard5"), so it can't answer this — the id lives in the
  // location, which changes on every soft nav between sessions.
  const location = useLocation();
  const activeSessionId = (() => {
    // Both bases: the copilot's own route, and the enterprise one that carries
    // the same kind of id (/ent-user/<id>). Without the second, the active-row
    // highlight on the enterprise rail could never match anything.
    const m = /^\/(?:TseDashboard5|ent-user)\/([^/?#]+)/.exec(location.pathname || "");
    if (!m) return "";
    try { return decodeURIComponent(m[1]); } catch { return m[1]; }
  })();
  // Read inside the mount-scoped effect below, which only reruns on `mode`
  // changing — not on every soft nav — so it would otherwise close over
  // whichever session was active the moment it first ran. Updated on every
  // render instead of depended on, the same trick `optimisticRef` uses.
  const activeSessionIdRef = useRef(activeSessionId);
  activeSessionIdRef.current = activeSessionId;
  const [expanded, setExpanded] = useState(() => localStorage.getItem(`${railPrefix}Expanded`) === "true");

  // Let parent pages know the actual rendered width (72px collapsed / 240px
  // expanded) — expansion persists across pages via localStorage, so a page
  // using fixed/absolute positioning next to the sidebar can't assume 72px.
  useEffect(() => {
    onExpandedChange?.(expanded);
  }, [expanded, onExpandedChange]);

  // Collapsed by default: recents are reference material, not the reason the
  // sidebar exists, and an open list would push the nav out of first glance.
  const [recentsOpen, setRecentsOpen] = useState(
    () => localStorage.getItem(`${railPrefix}Recents`) === "true"
  );

  // Code triage file tree expansion state
  const [expandedFolders, setExpandedFolders] = useState({ netbird: true, "netbird/client": true });
  const [fileTree, setFileTree] = useState(null);

  // Fetch file tree for code-triage mode
  useEffect(() => {
    if (mode === "code-triage" && !fileTree) {
      fetch("/code-agent-tree.json")
        .then(r => r.json())
        .then(data => setFileTree(data))
        .catch(() => setFileTree({ type: "folder", name: "netbird", path: "netbird", children: [] }));
    }
  }, [mode, fileTree]);

  // Batch has its own default (see the effect below) and must never read OR
  // write the copilot's `agentSideBarExpanded` / `agentSideBarRecents` keys —
  // a preference set on one side must not leak to the other next visit.
  const isBatchMode = !!batch;

  const toggleExpanded = () => {
    const next = !expanded;
    setExpanded(next);
    if (!isBatchMode) localStorage.setItem(`${railPrefix}Expanded`, String(next));
  };

  const toggleRecents = () => {
    // On the 72px rail there is no room for a list, so asking for recents opens
    // the sidebar too rather than doing nothing visible.
    if (!expanded) {
      setExpanded(true);
      if (!isBatchMode) localStorage.setItem(`${railPrefix}Expanded`, "true");
      setRecentsOpen(true);
      if (!isBatchMode) localStorage.setItem(`${railPrefix}Recents`, "true");
      return;
    }
    const next = !recentsOpen;
    setRecentsOpen(next);
    if (!isBatchMode) localStorage.setItem(`${railPrefix}Recents`, String(next));
  };

  // The batch case list is the ONLY way to pick a case now that the old Inbox
  // column is gone, so a user who has never expanded the rail (or last left it
  // collapsed from the copilot side) must not land on Batch Diagnostics with no
  // list and no way to open it. This forces the rail open + the list section
  // open ONCE per entry into batch mode — via React state only, never
  // localStorage (see isBatchMode above) — so the copilot's stored preference
  // is untouched. `batchDefaultAppliedRef` is what makes this a one-time
  // default rather than a forced state: if the user explicitly collapses
  // either while still in batch mode, this effect does not fire again (its
  // dependency, isBatchMode, hasn't changed) and the collapse sticks for the
  // rest of the visit. It only re-arms on the NEXT entry into batch mode.
  const batchDefaultAppliedRef = useRef(false);
  useEffect(() => {
    if (!isBatchMode) {
      batchDefaultAppliedRef.current = false;
      return;
    }
    if (batchDefaultAppliedRef.current) return;
    batchDefaultAppliedRef.current = true;
    setExpanded(true);
    setRecentsOpen(true);
  }, [isBatchMode]);

  const navigateTo = (path) => {
    if (onLinkClick) { onLinkClick(path); return; }
    window.location.assign(path);
  };

  // Switching WHICH copilot session is open must remount TseDashboard5, or its
  // sessionIdRef (seeded once at mount from `routeSessionId || genSessionId()`)
  // keeps pointing at the OLD session while the URL moves to the new one. App.js
  // keys TseDashboard5 on the route's session id for exactly this, so an in-app
  // navigate is enough now: React Router changes the key, the old instance
  // unmounts, and the new one re-derives its id from the URL.
  const navigateToSession = (path) => {
    navigate(path);
  };

  // Theme configuration for sidebar
  const sidebarTheme = {
    dark: {
      bg: 'bg-black',
      border: 'border-gray-800',
      logoBg: '#000000',
      hover: 'hover:bg-gray-900',
      buttonBg: 'bg-gray-900',
      buttonHover: 'hover:bg-red-600',
      text: 'text-gray-300',
      textHover: 'hover:text-white',
      themeButton: 'bg-gray-900 hover:bg-gray-800',
      themeButtonIcon: 'text-yellow-400',
    },
    light: {
      bg: 'bg-[#f6f6f1]',
      border: 'border-[#EBECE6]',
      logoBg: '#f6f6f1',
      hover: 'hover:bg-gray-100',
      buttonBg: 'bg-white',
      buttonHover: 'hover:bg-red-100',
      text: 'text-gray-600',
      textHover: 'hover:text-gray-900',
      themeButton: 'bg-white hover:bg-gray-50',
      themeButtonIcon: 'text-gray-600',
    }
  };

  const currentSidebarTheme = isDarkMode ? sidebarTheme.dark : sidebarTheme.light;

  // ── The enterprise rail's own palette ────────────────────────────────────
  // #4A90E2 is a MID blue, so nothing subtle survives on it: the greys this
  // rail used for secondary text and its cyan accents sit too close to the
  // background to read. Everything here is white or a white transparency —
  // one hue varied by opacity, which keeps it one surface rather than a light
  // theme with a blue patch behind it.
  //
  // Contrast: white on #4A90E2 is ~3.2:1, under the 4.5:1 AA wants for body
  // text at this size. Text is therefore held at FULL white with a medium
  // weight; dimming it to "look softer" is what would make it unreadable.
  //
  // Applied ONLY to the enterprise copilot's rail. `showRecents` is what
  // distinguishes it from the plain user chat, which keeps the light theme.
  const entRail = {
    bg: "bg-[#4A90E2]",
    border: "border-white/25",
    text: "text-white",
    textMuted: "text-white/75",
    rowHover: "hover:bg-white/15",
    rowActive: "bg-white/25",
    field: "bg-white/15 border-white/30 text-white placeholder-white/60",
    iconBtn: "bg-white/15 hover:bg-white/25 text-white",
  };
  const isEntRail = mode !== "agent" && !!showRecents;

  // ── Recent sessions: fetch from API on mount ──
  const [recentSessions, setRecentSessions] = useState(
    () => (recentsCacheOwner === currentOwner(surfaceScope) ? recentsCache : [])
  );
  const [recentsLoading, setRecentsLoading] = useState(false);
  // Whether the server had more rows than the rail draws. Decided by the fetch
  // returning a full page (RECENTS_FETCH_LIMIT = one more than is rendered) and
  // NOT by counting the list on screen: optimistic rows are spliced in locally,
  // so a user with exactly thirty saved chats and one in flight would otherwise
  // be shown a "View all" that leads to the same thirty rows.
  const [hasMoreSessions, setHasMoreSessions] = useState(false);
  // Read by the relabel ticker below so it can compare "would this row's label
  // actually change" without depending on `recentSessions` and re-arming the
  // interval on every list update.
  const recentSessionsRef = useRef(recentSessions);
  // Ids marked Closed locally (RECENTS_CLOSED_EVENT) that a fetch hasn't
  // confirmed yet. A fetch can come back with a stale `closed: false` for one
  // of these — Postgres lagging Redis through the write-behind outbox — and
  // without this the row would flip back to "open" until the next fetch
  // finally catches up.
  const closedLocallyRef = useRef(new Set());
  // Write-through: whatever is on screen is what a remount starts from.
  useEffect(() => {
    recentsCache = recentSessions;
    recentsCacheOwner = currentOwner(surfaceScope);
    recentSessionsRef.current = recentSessions;
  }, [recentSessions, surfaceScope]);
  // Bumped to re-run the effect below once its own timer fires — nothing else
  // about the row changes at that moment, so recentSessions alone wouldn't
  // trigger a re-render or a re-arm for a second still-pending row.
  const [savingLabelTick, setSavingLabelTick] = useState(0);
  // Forces a re-render when the "Saving…" label's timeout passes, so it
  // doesn't sit stale until some unrelated state change happens to redraw the
  // row. One timer for the whole list — armed for whichever pending row's
  // deadline is soonest, not one per row and not a poll — and rearmed (via
  // savingLabelTick above) for the next-soonest if more than one row is still
  // in flight when it fires. This never touches `entry.pending`; it only
  // decides whether the render below is allowed to say "Saving…".
  useEffect(() => {
    const now = Date.now();
    const deadlines = recentSessions
      .filter((s) => s.pending)
      // A row restored from optimisticCache across a remount may predate
      // `pendingSince` entirely — treat that as already-expired rather than
      // scheduling a timer for it (or worse, showing "Saving…" forever).
      .map((s) => (s.pendingSince || 0) + SAVING_LABEL_TIMEOUT_MS)
      .filter((deadline) => deadline > now);
    if (deadlines.length === 0) return undefined;
    const soonest = Math.min(...deadlines);
    const timer = setTimeout(() => setSavingLabelTick((t) => t + 1), soonest - now);
    return () => clearTimeout(timer);
  }, [recentSessions, savingLabelTick]);
  // Rows inserted locally on the first message of a chat, still unconfirmed by
  // the API. Kept out of state so a fetch landing mid-flight can reconcile
  // against the current set without depending on a stale closure: a fetched row
  // with the same id WINS (real title, real timestamp) and the optimistic one is
  // dropped; one the API doesn't know about yet is kept on top rather than
  // vanishing under the user. Seeded from the module cache for the same reason
  // the list is: a session switch remounts this component, and an optimistic row
  // inserted a second ago must not disappear because of it.
  const optimisticRef = useRef(recentsCacheOwner === currentOwner(surfaceScope) ? optimisticCache : []);
  const setOptimistic = useCallback((list) => {
    optimisticRef.current = list;
    optimisticCache = list;
  }, []);

  const formatRelativeTime = useCallback((iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return "Just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay === 1) return "Yesterday";
    if (diffDay < 7) return `${diffDay} days ago`;
    if (diffDay < 14) return "Last week";
    return d.toLocaleDateString();
  }, []);

  // Relative labels ("29m ago") are computed once, at fetch time, and stored
  // as a plain string alongside the raw `updatedAt` (see the comment at that
  // field). Nothing re-renders a row on its own just because a minute passed,
  // so without this the label would sit at whatever it said the moment it was
  // fetched — a session touched once and never reopened would say "Just now"
  // forever. One timer for the whole list, not one per row and not tied to
  // `recentSessions` (that would re-arm on every fetch/rename/delete for no
  // reason): it reads the live list via `recentSessionsRef` and only calls the
  // setter when a label actually changed, so an idle sidebar isn't re-rendered
  // every 30s for nothing.
  useEffect(() => {
    const RELABEL_INTERVAL_MS = 30000;
    const timer = setInterval(() => {
      const prev = recentSessionsRef.current;
      let changed = false;
      const next = prev.map((s) => {
        // No timestamp = an optimistic "Saving…" row, handled entirely by
        // pendingSince/SAVING_LABEL_TIMEOUT_MS above — this ticker must not
        // touch it.
        if (!s.updatedAt) return s;
        const label = formatRelativeTime(s.updatedAt);
        if (label === s.when) return s;
        changed = true;
        return { ...s, when: label };
      });
      if (changed) setRecentSessions(next);
    }, RELABEL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [formatRelativeTime]);

  useEffect(() => {
    // The enterprise rail runs the same fetch: its sessions come from the same
    // /api/copilot/sessions endpoint, which is owner-scoped and has no role
    // gate, so an enterprise user already gets exactly their own chats back.
    if (mode !== "agent" && !entRecents) return undefined;
    // The batch route has its own list (batch cases); skip the copilot recents
    // fetch there so it does not waste a request the sidebar will never show.
    if ((currentPath || "").startsWith("/TSEBatchDashboard")) return undefined;
    let cancelled = false;
    // Mount fires the spinner load, RECENTS_REFRESH_EVENT and window "focus"
    // both piggyback on the same fetcher — without guarding, that's 3-6 calls
    // to a ~600-900ms endpoint per page load. inFlight dedupes concurrent
    // callers onto the same request; lastFetchAt enforces a short cooldown so
    // a focus right after mount doesn't refire it. RECENTS_REFRESH_EVENT gets
    // a `force` path so a just-created session still shows up immediately.
    let inFlight = null;
    let lastFetchAt = 0;
    const COOLDOWN_MS = 5000;

    // The single forced refresh behind RECENTS_REFRESH_EVENT always fires too
    // early to see the row it is asking for: the server emits tse:idle BEFORE
    // it commits the turn, /api/copilot/sessions reads Postgres only, and the
    // Redis -> Postgres hop is write-behind through an outbox whose flusher can
    // block for seconds. So the fetch comes back without the session and the
    // optimistic row sits on "Saving…" forever — window focus is the only other
    // trigger and it is cooldown-gated.
    //
    // Bounded catch-up: three more tries, then stop. Deliberately not a poll —
    // it only runs while a row is genuinely unconfirmed, and it terminates
    // whether or not the flush ever lands.
    const PENDING_RETRY_DELAYS_MS = [2000, 6000, 12000];
    let retryTimers = [];
    const clearPendingRetries = () => {
      retryTimers.forEach((t) => clearTimeout(t));
      retryTimers = [];
    };
    const armPendingRetries = () => {
      // A later commit re-arms from scratch rather than stacking windows.
      clearPendingRetries();
      PENDING_RETRY_DELAYS_MS.forEach((ms) => {
        retryTimers.push(setTimeout(() => {
          // Confirmed in the meantime (or unmounted) — nothing left to chase.
          if (cancelled || optimisticRef.current.length === 0) return;
          fetchRecents({ force: true, isRetry: true });
        }, ms));
      });
    };

    // Every fetch flags itself, but only an EMPTY list draws the block spinner
    // (see the render): once there are rows, a refresh shows a small mark on the
    // Recents header and leaves the list where it is. Blanking it out from under
    // the reader is the thing to avoid.
    const fetchRecents = ({ force = false, isRetry = false } = {}) => {
      const now = Date.now();
      if (inFlight) return inFlight;
      if (!force && now - lastFetchAt < COOLDOWN_MS) return Promise.resolve();

      setRecentsLoading(true);
      inFlight = (async () => {
        try {
          const scope = surfaceScope ? `&surface=${encodeURIComponent(surfaceScope)}` : "";
          const res = await authFetch(`/api/copilot/sessions?limit=${RECENTS_FETCH_LIMIT}${scope}`);
          if (!cancelled && res.ok) {
            const data = await res.json();
            const fetched = (data.sessions || []).map((s) => {
              // Locally-known Closed can outrun this fetch (see closedLocallyRef
              // above); once the server itself says closed, its value rules and
              // the local marker is no longer needed.
              const locallyClosed = closedLocallyRef.current.has(s.session_id);
              if (s.closed) closedLocallyRef.current.delete(s.session_id);
              return {
                id: s.session_id,
                title: s.title || "Untitled session",
                when: formatRelativeTime(s.updated_at),
                // The raw timestamp is kept alongside the rendered one: `when` is a
                // sentence ("20h ago", "Last week") and cannot be sorted or bucketed
                // by day, which is what the rail's order and the All-chats modal
                // both need.
                updatedAt: s.updated_at || null,
                product: s.product || "",
                closed: !!s.closed || locallyClosed,
              };
            });
            const confirmed = new Set(fetched.map((s) => s.id));
            setHasMoreSessions(fetched.length >= RECENTS_FETCH_LIMIT);
            setOptimistic(optimisticRef.current.filter((o) => !confirmed.has(o.id)));
            setRecentSessions((prev) => {
              // Postgres lags Redis through the write-behind outbox (see the
              // comment above this effect), so a row this fetch just brought
              // back can carry an `updated_at` OLDER than what a local bump
              // (onSaved below, or a previous fetch) already knows for the
              // same id — a rename, a resumed chat, or the active-session
              // freshen. Taking the fetched value unconditionally would walk
              // that row's timestamp BACKWARDS the instant the outbox is
              // behind, undoing the freshen the moment it lands. Per id, keep
              // whichever of (fetched, locally-known) is newer.
              const localById = new Map(prev.map((s) => [s.id, s.updatedAt]));
              const merged = fetched.map((s) => {
                const local = localById.get(s.id);
                const localMs = local ? new Date(local).getTime() : NaN;
                const fetchedMs = s.updatedAt ? new Date(s.updatedAt).getTime() : NaN;
                if (!Number.isNaN(localMs) && (Number.isNaN(fetchedMs) || localMs > fetchedMs)) {
                  return { ...s, updatedAt: local, when: formatRelativeTime(local) };
                }
                return s;
              });
              return [...optimisticRef.current, ...merged].sort(byUpdatedDesc);
            });
          }
          lastFetchAt = Date.now();
        } catch {
          // Silently degrade — sidebar shows "No recent chats"
        } finally {
          if (!cancelled) {
            setRecentsLoading(false);
            // A retry never re-arms: the window is fixed at three requests.
            if (!isRetry && optimisticRef.current.length > 0) armPendingRetries();
          }
          inFlight = null;
        }
      })();
      return inFlight;
    };

    fetchRecents({ force: true });

    // A session only enters Recents once its first turn has been committed AND
    // flushed to Postgres, which happens seconds AFTER this sidebar mounted.
    // Fetching once on mount therefore meant a brand-new chat never appeared
    // until a full page reload — the session was in the database the whole
    // time, the sidebar just never asked again. Forced: a new session must
    // show up right away, cooldown or not.
    const onSaved = () => {
      // Freshen the OPEN session's row immediately, before the fetch below
      // even lands. RECENTS_REFRESH_EVENT means a turn just committed — the
      // engineer is looking at this row right now — but the forced fetch that
      // follows almost always reads Postgres before the write-behind outbox
      // has flushed it (see the big comment above this effect), so waiting on
      // it alone leaves "29m ago" on screen for a session that's actively
      // streaming. This is additive, not a replacement for the fetch: the
      // fetch is still what reconciles title/product and everything else, and
      // its merge (above) is what keeps this bump from being clobbered by a
      // stale `updated_at` coming back from that same fetch.
      const activeId = activeSessionIdRef.current;
      if (activeId) {
        // A turn just committed for this id, so the server has revived it
        // (or it was never actually closed) — it is live again.
        closedLocallyRef.current.delete(activeId);
        const now = new Date().toISOString();
        setRecentSessions((prev) => {
          let changed = false;
          const next = prev.map((s) => {
            if (s.id !== activeId) return s;
            changed = true;
            return { ...s, updatedAt: now, when: formatRelativeTime(now) };
          });
          return changed ? next.sort(byUpdatedDesc) : prev;
        });
      }
      fetchRecents({ force: true });
    };
    window.addEventListener(RECENTS_REFRESH_EVENT, onSaved);
    // Covers the other tab: start a chat in one, switch back to this one.
    // Not forced — the cooldown is enough here, and it keeps a rapid
    // alt-tab from re-hitting the endpoint on every focus.
    const onFocus = () => fetchRecents();
    window.addEventListener("focus", onFocus);

    // The first message of a chat: show the row NOW, with the message as a
    // stand-in title. No fetch — the turn isn't committed yet, so asking would
    // only burn a request and still come back without it.
    const onOptimistic = (e) => {
      const id = e?.detail?.sessionId;
      if (!id || cancelled) return;
      const row = {
        id,
        title: truncateTitle(e.detail.title),
        when: "Just now",
        // Null, not Date.now(): the row is not in Postgres yet, and byUpdatedDesc
        // reads a missing timestamp as "created just now" and floats it to the top.
        updatedAt: null,
        product: "",
        pending: true,
        // When the "Saving…" label may stop saying so — see SAVING_LABEL_TIMEOUT_MS.
        // Only the label reads this; `pending` itself is untouched by the timeout.
        pendingSince: Date.now(),
      };
      setRecentSessions((prev) => {
        // Already listed (a resumed session, or a duplicate dispatch) — the
        // real row is better than this one, so leave it alone.
        if (prev.some((s) => s.id === id)) return prev;
        setOptimistic([row, ...optimisticRef.current.filter((o) => o.id !== id)]);
        return [row, ...prev];
      });
    };
    window.addEventListener(RECENTS_OPTIMISTIC_EVENT, onOptimistic);

    // A session retiring (a `/clear`, or the server's feedback prompt on a
    // natural close) — mark it Closed now rather than waiting on the fetch
    // that would otherwise read Postgres before the outbox has flushed it.
    // The row may not be in the list yet (still an unconfirmed optimistic
    // row) — closedLocallyRef covers that case, so this must not bail when
    // the id isn't found.
    const onClosed = (e) => {
      const id = e?.detail?.sessionId;
      if (!id || cancelled) return;
      closedLocallyRef.current.add(id);
      setRecentSessions((prev) => prev.map((s) => (s.id === id ? { ...s, closed: true } : s)));
    };
    window.addEventListener(RECENTS_CLOSED_EVENT, onClosed);

    return () => {
      cancelled = true;
      clearPendingRetries();
      window.removeEventListener(RECENTS_REFRESH_EVENT, onSaved);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener(RECENTS_OPTIMISTIC_EVENT, onOptimistic);
      window.removeEventListener(RECENTS_CLOSED_EVENT, onClosed);
    };
  }, [mode, entRecents, surfaceScope, currentPath, formatRelativeTime, setOptimistic]);

  // Opening/resuming a session deliberately does NOT move it in Recents. The
  // rail mirrors real activity: the server only advances `updated_at` when a
  // turn commits (or on a rename/edit), so merely viewing a session must leave
  // its position and its relative-time label untouched. An earlier version
  // stamped `updatedAt = now` on resume, which made every session read as
  // "Just now" at the top of the list the moment it was clicked — a lie about
  // when the engineer last worked on it. Do not reintroduce that bump here.
  // ── Recents filter ──
  // Client-side only: the rail holds at most RECENTS_VISIBLE_LIMIT rows, so there
  // is nothing to debounce and nothing to ask the server for. Searching the FULL
  // history is a different job and belongs to the All-chats modal, which does its
  // own fetch.
  const [recentsFilter, setRecentsFilter] = useState("");

  // "View all chats" — the whole history, grouped by day, with its own search
  // and date filters. Kept closed-by-default state rather than a route so it can
  // be opened from the rail on any page the sidebar renders on.
  const [allChatsOpen, setAllChatsOpen] = useState(false);

  // ── Per-row rename / delete ──
  // Hover-revealed icons rather than a "…" popover menu: the rows live inside
  // an `overflow-y-auto` scroller, so anything anchored to a row and taller
  // than it gets clipped unless it is portalled and position-tracked. The rows
  // already shift colour on `group-hover:`, so this reuses an affordance the
  // sidebar is built around. `group-focus-within` keeps it keyboard-reachable —
  // the buttons are only transparent, never hidden, so they stay in tab order.
  const [renamingId, setRenamingId] = useState("");
  const [renameDraft, setRenameDraft] = useState("");
  const [confirmingDeleteId, setConfirmingDeleteId] = useState("");
  // { id, message } — shown in place of the row's timestamp line.
  const [rowError, setRowError] = useState(null);
  // Escape must not be undone by the blur that immediately follows it, and the
  // blur React fires as the input unmounts after Enter must not double-commit.
  const skipRenameBlurRef = useRef(false);

  useEffect(() => {
    if (!rowError) return undefined;
    const t = setTimeout(() => setRowError(null), 4000);
    return () => clearTimeout(t);
  }, [rowError]);

  const startRename = useCallback((entry) => {
    // The previous edit may have ended on Enter/Escape, where React never
    // delivers the unmount blur — so the flag can still be set. Clear it here
    // or this rename's blur would be swallowed and never commit.
    skipRenameBlurRef.current = false;
    setRowError(null);
    setConfirmingDeleteId("");
    setRenameDraft(entry.title);
    setRenamingId(entry.id);
  }, []);

  const cancelRename = useCallback(() => {
    skipRenameBlurRef.current = true;
    setRenamingId("");
  }, []);

  const commitRename = useCallback(async (entry) => {
    const next = renameDraft.replace(/\s+/g, " ").trim();
    setRenamingId("");
    // Empty or unchanged: nothing worth a request, and an empty title would
    // leave the row unidentifiable.
    if (!next || next === entry.title) return;
    const prevTitle = entry.title;
    setRowError(null);
    setRecentSessions((prev) => prev.map((s) => (s.id === entry.id ? { ...s, title: next } : s)));
    try {
      const res = await authFetch(`/api/copilot/sessions/${encodeURIComponent(entry.id)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: next }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch {
      setRecentSessions((prev) => prev.map((s) => (s.id === entry.id ? { ...s, title: prevTitle } : s)));
      setRowError({ id: entry.id, message: "Rename failed" });
    }
  }, [renameDraft]);

  const deleteSession = useCallback(async (entry) => {
    setConfirmingDeleteId("");
    setRowError(null);
    // Remembered so a failed request can put the row back where it was rather
    // than at the top of the list.
    let removed = null;
    setRecentSessions((prev) => {
      const idx = prev.findIndex((s) => s.id === entry.id);
      if (idx === -1) return prev;
      removed = { idx, row: prev[idx] };
      return prev.filter((s) => s.id !== entry.id);
    });
    try {
      const res = await authFetch(`/api/copilot/sessions/${encodeURIComponent(entry.id)}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // The open session just stopped existing — open a blank chat rather than
      // leaving the dashboard pointed at a deleted id.
      if (entry.id === activeSessionId) navigate(entRecents ? ENT_BASE_PATH : NEW_SESSION_PATH);
    } catch {
      setRecentSessions((prev) => {
        if (!removed || prev.some((s) => s.id === entry.id)) return prev;
        const next = prev.slice();
        next.splice(Math.min(removed.idx, next.length), 0, removed.row);
        return next;
      });
      setRowError({ id: entry.id, message: "Delete failed" });
    }
  }, [activeSessionId, entRecents, navigate]);

  // Code triage mode: show netbird file tree skeleton
  if (mode === "code-triage") {
    const toggleFolder = (path) => setExpandedFolders(prev => ({ ...prev, [path]: !prev[path] }));

    const formatSize = (bytes) => {
      if (!bytes) return "";
      if (bytes < 1024) return `${bytes}B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}K`;
      return `${(bytes / (1024 * 1024)).toFixed(1)}M`;
    };

    const renderTree = (items, depth = 0) => items.map(item => {
      const isFolder = item.type === "folder";
      const isOpen = expandedFolders[item.path];
      const hasChildren = item.children?.length > 0;
      return (
        <div key={item.path}>
          <button
            type="button"
            onClick={() => hasChildren && toggleFolder(item.path)}
            className={`flex w-full items-center gap-1.5 rounded px-2 py-0.5 text-left text-[11px] transition-colors hover:bg-cyan-50 ${isFolder ? "text-gray-600 font-medium" : "text-gray-500"}`}
            style={{ paddingLeft: `${8 + depth * 12}px` }}
          >
            {hasChildren ? (
              <ChevronRight size={10} className={`flex-shrink-0 transition-transform ${isOpen ? "rotate-90" : ""}`} />
            ) : (
              <span className="w-2.5" />
            )}
            <span className="truncate">{item.name}{isFolder ? "/" : ""}</span>
            {item.size && <span className="ml-auto flex-shrink-0 text-[9px] text-gray-400">{formatSize(item.size)}</span>}
          </button>
          {isOpen && hasChildren && renderTree(item.children, depth + 1)}
        </div>
      );
    });

    return (
      <aside
        className="relative z-[60] flex h-screen flex-col border-r bg-white"
        style={{ width: expanded ? "280px" : "72px", minWidth: expanded ? "280px" : "72px" }}
      >
        {/* Header */}
        <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-100 px-4 py-3">
          <div className="flex items-center gap-2">
            <img src="/A_bgless.png" alt="Logo" className="h-8 w-8" />
            {expanded && <span className="text-[11px] text-gray-400">v{appVersion}</span>}
          </div>
          <button
            type="button"
            onClick={toggleExpanded}
            className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
            title={expanded ? "Collapse" : "Expand"}
          >
            {expanded ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
          </button>
        </div>

        {/* File tree */}
        {expanded && (
          <div className="flex flex-1 flex-col overflow-hidden">
            <div className="flex-shrink-0 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
              Repository
            </div>
            <div className="flex-1 overflow-y-auto px-1 pb-4">
              {fileTree ? renderTree([fileTree]) : (
                <div className="flex items-center gap-2 px-3 py-2 text-[11px] text-gray-400">
                  <Loader2 size={12} className="animate-spin" /> Loading...
                </div>
              )}
            </div>
          </div>
        )}
      </aside>
    );
  }

  // For agent mode, use theme-aware design
  if (mode === "agent") {

    const AGENT_ITEMS = [
      {
        key: "documentation",
        label: "Help Documentation",
        icon: <BookOpen size={20} />,
        onClick: () => window.open("/documentation", "_blank"),
        path: "/documentation",
        colorClass: "text-gray-500 hover:bg-cyan-50 hover:text-cyan-700",
      },
      // Dashboard2 ("Diagnostic Agent", /TseDashboard2) and Copilot v3
      // (/TseDashboard5) are both deliberately absent from the nav: the copilot
      // is reached through "New Copilot Session" and Recents below, which are the
      // entry points that carry a session. Both routes in App.js are left
      // intact, so the pages still open for anyone with a direct link.
      {
        key: "batch",
        label: "Batch Diagnostics",
        icon: <img src={currentPath === "/TSEBatchDashboard" ? "/icons/batch-teal.png" : "/icons/batch-white.png"} alt="" className="w-5 h-5 rounded flex-shrink-0" />,
        onClick: () => navigateTo("/TSEBatchDashboard"),
        path: "/TSEBatchDashboard",
        colorClass: "text-gray-500 hover:bg-cyan-50 hover:text-cyan-700",
        active: currentPath === "/TSEBatchDashboard",
        activeClass: "bg-cyan-500 text-white",
      },
      // Open Cases Analysis (/open-cases) is deliberately hidden from the nav.
      // The route in App.js is left intact, so the page still opens by direct link.
      {
        key: "escalation",
        label: "Escalation Dashboard",
        icon: <img src={currentPath === "/agent" ? "/icons/escalation-teal.png" : "/icons/escalation-white.png"} alt="" className="w-5 h-5 rounded flex-shrink-0" />,
        onClick: () => navigateTo("/agent"),
        path: "/agent",
        colorClass: "text-gray-500 hover:bg-cyan-50 hover:text-cyan-700",
        active: currentPath === "/agent",
        activeClass: "bg-cyan-500 text-white",
      },
      // The TSE's own review backlog. It belongs in the agent nav, not only the
      // admin one: the reviewer is whoever ran the session, because "did this
      // step work?" is a question only they can answer.
      {
        key: "learning",
        label: "Learning Trails",
        icon: <GraduationCap size={20} />,
        onClick: () => navigateTo("/learning"),
        path: "/learning",
        colorClass: "text-gray-500 hover:bg-cyan-50 hover:text-cyan-700",
        active: currentPath.startsWith("/learning"),
        activeClass: "bg-cyan-500 text-white",
      },
      {
        key: "api-keys",
        label: "API Keys",
        icon: <KeyRound size={20} />,
        onClick: () => window.open("/api-keys", "_blank"),
        path: "/api-keys",
        colorClass: "text-gray-500 hover:bg-cyan-50 hover:text-cyan-700",
        active: currentPath === "/api-keys",
        activeClass: "bg-cyan-500 text-white",
      },
    ];

    // Whether the sidebar shows batch cases instead of copilot recents. Driven
    // by the prop, not by the path: the owner of the data decides what the rail
    // renders, so there is no window where the route says "batch" but no batch
    // state has arrived.
    const isBatchList = !!batch;

    // The one list the rest of the block renders: optimistic rows and fetched
    // rows have already been merged into `recentSessions`, so filtering here
    // cannot leave one of the two sources unfiltered.
    const filterQuery = recentsFilter.trim().toLowerCase();

    // ── Batch case list ──
    // Already searched AND status-filtered by BatchMode (it owns both inputs),
    // so the rail renders `filteredCases` verbatim rather than re-deriving a
    // subset the header's "6/70" count would then disagree with.
    const batchCases = isBatchList ? (batch.cases || []) : [];
    const matchedBatchCases = isBatchList ? (batch.filteredCases || []) : [];

    // ── Copilot recents (shown when NOT isBatchList) ──
    const matchedSessions = !isBatchList
      ? (filterQuery
          ? recentSessions.filter(
              (s) =>
                String(s.title || "").toLowerCase().includes(filterQuery) ||
                String(s.product || "").toLowerCase().includes(filterQuery)
            )
          : recentSessions)
      : [];
    // Re-sorted here as well as at the fetch: a rename rewrites a row in place
    // and the optimistic splice puts a row at index 0 regardless of the rest, so
    // this is the last point before paint where the order can be guaranteed.
    const sortedSessions = matchedSessions.slice().sort(byUpdatedDesc);
    const visibleSessions = sortedSessions.slice(0, RECENTS_VISIBLE_LIMIT);
    // Suppressed while a filter is active: "View all" would then read as "all of
    // this subset", and the modal searches the whole history rather than these
    // thirty-one rows.
    const hasMoreChats = !isBatchList && !filterQuery && hasMoreSessions;

    return (
      <aside
        className={`relative z-[60] flex flex-col overflow-hidden ${currentSidebarTheme.bg} ${currentSidebarTheme.border} border-r h-screen py-4 transition-all duration-300 ease-in-out flex-shrink-0`}
        style={{ width: expanded ? 240 : 72, minWidth: expanded ? 240 : 72 }}
      >
        {/* Top: Logo + expand/collapse toggle */}
        <div className={`mb-4 flex items-center ${expanded ? "justify-between px-4" : "justify-center"}`}>
          <img
            src="/A_bgless.png"
            alt="Arta Logo"
            style={{ display: 'block', maxWidth: '36px', height: 'auto', flexShrink: 0 }}
          />
          {/* The version, where the word "Agent" used to be. Keeps `flex-1` so
              the collapse button stays pinned right whatever the string's
              length — "..." while it resolves, "unknown" if the fetch fails.
              Deliberately quiet type: it is a reference detail for support, not
              a heading. */}
          {expanded && (
            <span
              className={`ml-2 flex-1 text-[11px] font-medium tracking-wide opacity-60 ${currentSidebarTheme.text}`}
              title="App version"
            >
              v{appVersion}
            </span>
          )}
          <button
            onClick={toggleExpanded}
            className={`w-7 h-7 flex items-center justify-center rounded-full border ${currentSidebarTheme.border} ${currentSidebarTheme.buttonBg} text-gray-400 hover:text-cyan-500 transition flex-shrink-0`}
            title={expanded ? "Collapse sidebar" : "Expand sidebar"}
          >
            {expanded ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
          </button>
        </div>

        {/* Navigation sits directly under the logo. It used to be pushed to the
            bottom by a flex-1 spacer, which put the primary destinations in the
            last place the eye lands and left the top two-thirds empty. */}
        {!hideActions && (
          <div className={`flex flex-col gap-2 ${expanded ? "px-2" : "items-center"}`}>
            {AGENT_ITEMS.map((item) => (
              <a
                key={item.key}
                href={item.path}
                onClick={(e) => {
                  if (!e.ctrlKey && !e.metaKey && !e.shiftKey && e.button !== 1) {
                    e.preventDefault();
                    item.onClick();
                  }
                }}
                title={!expanded ? item.label : undefined}
                className={`flex items-center gap-3 rounded-lg shadow-sm transition-all duration-300 ${
                  item.active ? item.activeClass : `${currentSidebarTheme.buttonBg} ${item.colorClass}`
                } ${expanded ? "px-3 py-2.5 w-full" : "w-10 h-10 justify-center"}`}
              >
                <span className="flex-shrink-0">{item.icon}</span>
                {expanded && <span className="text-sm font-medium whitespace-nowrap">{item.label}</span>}
              </a>
            ))}
          </div>
        )}

        {/* Recents. Collapsed to a single row by default so the nav above stays
            the first thing read. Owns flex-1 whether open or shut, so it is the
            element that absorbs spare height and pins the footer to the bottom —
            no separate spacer div to keep in sync. */}
        {/* mt-2 below matches the nav list's own gap-2 above. mt-4 made the one
            gap between "API Keys" and "New Copilot Session" twice every other
            gap in the rail, so the button read as detached from the stack it
            belongs to. */}
        {!hideActions && !hideRecents && (
          <div className={`mt-2 flex min-h-0 flex-1 flex-col ${expanded ? "px-2" : "items-center"}`}>
            {/* New Copilot Session -> the blank-session path. Nothing is created by
                clicking this: /TseDashboard5/new draws an empty copilot with an
                id held in reserve, and only the first message turns it into a
                session with an address and a Recents row (App.js:
                TseDashboard5Route, TseDashboard5.promoteSessionUrl).
                Rendered on EVERY agent page including batch: the batch screen
                has no other route back to the copilot, so hiding it there
                stranded the user. */}
            <button
              type="button"
              onClick={() => navigateToSession(NEW_SESSION_PATH)}
              title={!expanded ? "New Copilot Session" : undefined}
              // gap-3 / px-3 py-2.5 / size-20 icon / text-sm are copied from the
              // AGENT_ITEMS links above (Help Documentation, API Keys …). This
              // button sits directly under them, so anything smaller reads as a
              // lesser control — it is the primary action in the rail. Only the
              // colours differ, which is what marks it out.
              className={`mb-2 flex items-center rounded-lg border border-cyan-200 bg-white text-cyan-700 shadow-sm transition-all duration-300 hover:bg-cyan-50 hover:border-cyan-300 ${
                expanded ? "gap-3 px-3 py-2.5 w-full" : "w-10 h-10 justify-center"
              }`}
            >
              <Plus size={20} className="flex-shrink-0" />
              {expanded && (
                <span className="text-sm font-medium whitespace-nowrap flex-1 text-left">
                  New Copilot Session
                </span>
              )}
            </button>
            {/* The delete-all control is a SIBLING of the collapse toggle, not
                a child: a button inside a button is invalid HTML and the inner
                one would not be reachable by keyboard. */}
            <div className={`flex items-center ${expanded ? "w-full gap-0.5" : "justify-center"}`}>
              <button
                type="button"
                onClick={toggleRecents}
                title={!expanded ? (isBatchList ? "Batch Sessions" : "Recents") : undefined}
                aria-expanded={expanded && recentsOpen}
                className={`flex items-center rounded-lg transition-all duration-300 text-gray-500 hover:bg-cyan-50 hover:text-cyan-700 ${
                  expanded ? "gap-2 px-3 py-2 min-w-0 flex-1" : "w-10 h-10 justify-center"
                }`}
              >
                {/* MessageSquare is geometrically centred (its art has 3 units of
                    padding top and bottom in a 24x24 viewBox), but the bubble BODY
                    sits above the tail, so its visual mass reads ~1px high against
                    the cap-height band of the uppercase label. Nudge, purely optical. */}
                <MessageSquare size={expanded ? 15 : 20} className="relative -top-px flex-shrink-0" />
                {expanded && (
                  <>
                    <span className="text-[11px] font-semibold uppercase tracking-wider flex-1 min-w-0 truncate text-left">
                      {isBatchList ? "Batch Sessions" : "Recents"}
                      {/* Mirrors the old Inbox header: "6/70" while a search or
                          status filter is narrowing the list, plain "70"
                          otherwise. */}
                      {isBatchList && batchCases.length > 0 && (
                        <span className="ml-1 font-normal normal-case tracking-normal text-gray-400">
                          {matchedBatchCases.length !== batchCases.length
                            ? `${matchedBatchCases.length}/${batchCases.length}`
                            : batchCases.length}
                        </span>
                      )}
                    </span>
                    {/* Refresh indicator lives on the header, not over the list,
                        so the rows below stay readable and clickable throughout. */}
                    {isBatchList
                      ? (batch.loading && batchCases.length > 0 && (
                          <Loader2 size={11} className="flex-shrink-0 animate-spin text-gray-300" />
                        ))
                      : (recentsLoading && recentSessions.length > 0 && (
                          <Loader2 size={11} className="flex-shrink-0 animate-spin text-gray-300" />
                        ))}
                    <ChevronDown
                      size={14}
                      className={`flex-shrink-0 transition-transform duration-200 ${recentsOpen ? "" : "-rotate-90"}`}
                    />
                  </>
                )}
              </button>
              {/* Bulk delete. Hidden on the 72px rail, where there is no list to
                  act on. The confirmation is BatchMode's own ConfirmDialog —
                  this only asks for it. */}
              {isBatchList && expanded && batchCases.length > 0 && (
                <button
                  type="button"
                  onClick={batch.onDeleteAll}
                  disabled={batch.deleting}
                  title="Delete all batch cases"
                  aria-label="Delete all batch cases"
                  className="flex-shrink-0 rounded p-1 text-gray-400 transition-colors hover:bg-red-100 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Trash2 size={12} />
                </button>
              )}
            </div>

            {expanded && recentsOpen && (
              <div className="mt-1 flex min-h-0 flex-1 flex-col">
                {/* Filter sits above the scroller, not inside it, so it stays
                    put while the list moves under it. The batch list's query is
                    owned by BatchMode (it does the filtering), the copilot one
                    by `recentsFilter` here — one input, two sources. */}
                <div className="relative mb-1 flex-shrink-0 px-1">
                  <Search
                    size={12}
                    aria-hidden="true"
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-300"
                  />
                  <input
                    type="text"
                    value={isBatchList ? (batch.search || "") : recentsFilter}
                    onChange={(e) => (isBatchList ? batch.onSearchChange(e.target.value) : setRecentsFilter(e.target.value))}
                    placeholder={isBatchList ? "Search cases" : "Search chats"}
                    aria-label={isBatchList ? "Search batch cases" : "Search recent chats"}
                    className="w-full rounded-lg border border-gray-200 bg-white py-1.5 pl-7 pr-6 text-[12px] leading-tight text-gray-600 placeholder-gray-400 outline-none transition-colors focus:border-cyan-300"
                  />
                  {(isBatchList ? !!batch.search : !!recentsFilter) && (
                    <button
                      type="button"
                      onClick={() => (isBatchList ? batch.onSearchChange("") : setRecentsFilter(""))}
                      title="Clear search"
                      aria-label="Clear search"
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-gray-400 transition-colors hover:text-cyan-700"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>

                {/* Status pills, ported from the old Inbox column. Compact
                    enough for a 240px rail: a pill pair plus "clear" on the
                    same line. Clicking the active pill clears it, so the filter
                    is its own off-switch. */}
                {isBatchList && (
                  <div className="mb-1 flex flex-shrink-0 items-center gap-1 px-1">
                    {["open", "closed"].map((sVal) => (
                      <button
                        key={sVal}
                        type="button"
                        aria-pressed={batch.statusFilter === sVal}
                        onClick={() => batch.onStatusFilterChange(batch.statusFilter === sVal ? "" : sVal)}
                        className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase transition ${
                          batch.statusFilter === sVal
                            ? sVal === "open"
                              ? "border-emerald-300 bg-emerald-100 text-emerald-700"
                              : "border-gray-300 bg-gray-200 text-gray-600"
                            : "border-gray-200 bg-white text-gray-400 hover:border-gray-300"
                        }`}
                      >
                        {sVal}
                      </button>
                    ))}
                    {(batch.statusFilter || batch.search) && (
                      <button
                        type="button"
                        onClick={() => { batch.onSearchChange(""); batch.onStatusFilterChange(""); }}
                        className="ml-auto rounded px-1 text-[10px] text-gray-400 transition-colors hover:text-red-500"
                      >
                        clear
                      </button>
                    )}
                  </div>
                )}

                <div className="relative min-h-0 flex-1">
                <div className="absolute inset-0 overflow-y-auto d5-recents-scroll pb-3">
                {/* Loading / empty states — batch and copilot share the same
                    visual slot; which one paints depends on isBatchList.
                    Only the very first load has nothing to show. A refresh
                    keeps the previous list on screen — blanking it out (or
                    replacing it with a spinner) under a reader who is mid-click
                    is worse than a slightly stale row, and the transcript's own
                    loading state lives in the Copilot panel now, not here. */}
                {isBatchList ? (
                  <>
                    {batch.loading && batchCases.length === 0 && (
                      <div className="flex items-center justify-center py-4 text-gray-400">
                        <Loader2 size={16} className="animate-spin" />
                      </div>
                    )}
                    {!batch.loading && batchCases.length === 0 && (
                      <p className="px-3 py-3 text-[12px] text-gray-400">No batch cases</p>
                    )}
                    {batchCases.length > 0 && matchedBatchCases.length === 0 && (
                      <p className="px-3 py-3 text-[12px] text-gray-400">No matching cases</p>
                    )}
                  </>
                ) : (
                  <>
                    {recentsLoading && recentSessions.length === 0 && (
                      <div className="flex items-center justify-center py-4 text-gray-400">
                        <Loader2 size={16} className="animate-spin" />
                      </div>
                    )}
                    {!recentsLoading && recentSessions.length === 0 && (
                      <p className="px-3 py-3 text-[12px] text-gray-400">No recent chats</p>
                    )}
                    {recentSessions.length > 0 && visibleSessions.length === 0 && (
                      <p className="px-3 py-3 text-[12px] text-gray-400">No matching chats</p>
                    )}
                  </>
                )}
                {/* ── Batch case rows ── */}
                {isBatchList && matchedBatchCases.map((c) => {
                  const isActive = !!batch.activeCaseId && batch.activeCaseId === c.case_id;
                  return (
                    <div
                      key={c.case_id}
                      className={`group flex w-full items-center rounded-lg text-left transition-colors ${
                        isActive ? "bg-cyan-50" : "hover:bg-cyan-100"
                      }`}
                    >
                      <button
                        type="button"
                        title={c.subject || "Untitled"}
                        aria-current={isActive ? "page" : undefined}
                        onClick={() => batch.onSelectCase(c)}
                        className="flex min-w-0 flex-1 items-center gap-2 px-3 py-2 text-left"
                      >
                        <MessageSquare
                          size={13}
                          className={`flex-shrink-0 ${isActive ? "text-cyan-600" : "text-gray-300 group-hover:text-cyan-600"}`}
                        />
                        <span className="min-w-0 flex-1">
                          <span className={`block truncate text-[13px] leading-tight ${
                            isActive ? "font-medium text-cyan-800" : "text-gray-600 group-hover:text-cyan-800"
                          }`}>
                            {c.subject || "Untitled"}
                          </span>
                          <span className="flex items-center gap-1 text-[10px] leading-tight text-gray-400">
                            <span className="min-w-0 truncate">
                              {`${c.case_id} · ${formatRelativeTime(c.created_at)}`}
                            </span>
                            {/* Status is a dot, not the Inbox's word-pill: at
                                240px the pill pushed the case id out of the
                                row. Colour carries the same OPEN/CLOSED split,
                                and `title` keeps it readable for assistive
                                tech. */}
                            {c.status && (
                              <span
                                title={c.status}
                                aria-label={c.status}
                                className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${
                                  c.status === "open" ? "bg-emerald-400" : "bg-gray-300"
                                }`}
                              />
                            )}
                          </span>
                        </span>
                      </button>
                      {/* Same hover-revealed affordance the copilot rows use —
                          delete only: a batch case has no rename endpoint. The
                          confirmation is BatchMode's ConfirmDialog rather than
                          the rail's inline "Delete? Yes/Cancel", so the user is
                          not asked the same question twice. */}
                      <span className="flex flex-shrink-0 items-center pr-1.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
                        <button
                          type="button"
                          title="Delete"
                          aria-label={`Delete ${c.subject || c.case_id}`}
                          disabled={batch.deleting}
                          onClick={(e) => {
                            e.stopPropagation();
                            batch.onDeleteCase(c.case_id);
                          }}
                          className="rounded p-1 text-gray-400 transition-colors hover:bg-red-100 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          <Trash2 size={12} />
                        </button>
                      </span>
                    </div>
                  );
                })}
                {/* ── Copilot recents rows ── */}
                {!isBatchList && visibleSessions.map((entry) => {
                  // Same cyan family the nav uses for its current page, one step
                  // softer: this is a row in a list, not a primary destination,
                  // so a filled cyan-500 bar would out-shout the nav item above.
                  const isActive = !!activeSessionId && entry.id === activeSessionId;
                  const isRenaming = renamingId === entry.id;
                  const isConfirming = confirmingDeleteId === entry.id;
                  const error = rowError && rowError.id === entry.id ? rowError.message : "";
                  // Genuinely still saving: pending AND inside SAVING_LABEL_TIMEOUT_MS
                  // of its pendingSince. A missing pendingSince (a row carried over
                  // from optimisticCache by a remount that predates this field) is
                  // treated as already-expired rather than showing "Saving…" forever.
                  const isSavingLabel = !!entry.pending
                    && !!entry.pendingSince
                    && Date.now() - entry.pendingSince < SAVING_LABEL_TIMEOUT_MS;
                  return (
                    // A div, not a button: the row now carries its own controls,
                    // and a button inside a button is neither valid nor
                    // keyboard-navigable. The navigate target is the inner
                    // button, which keeps the row's padding as click area.
                    <div
                      key={entry.id}
                      // Selected and hover are two DEPTHS of one colour, not a
                      // fill plus an outline — the ring made the open chat the
                      // only bordered thing in the rail. They must also DIFFER:
                      // both were cyan-50 once, so hovering any row made it look
                      // selected and two rows looked open at once.
                      // Selected is the calmer cyan-50 because it persists;
                      // hover is the stronger cyan-100 because it is momentary
                      // and follows the pointer.
                      className={`group flex w-full items-center rounded-lg text-left transition-colors ${
                        isActive ? "bg-cyan-50" : "hover:bg-cyan-100"
                      }`}
                    >
                      {isConfirming ? (
                        // Inline confirm rather than window.confirm: a native
                        // dialog blocks the whole tab and cannot be styled.
                        <div className="flex w-full items-center gap-1 px-3 py-2">
                          <span className="min-w-0 flex-1 truncate text-[12px] text-gray-600">Delete?</span>
                          <button
                            type="button"
                            onClick={() => deleteSession(entry)}
                            className="flex-shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium text-red-600 transition-colors hover:bg-red-50"
                          >
                            Yes
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmingDeleteId("")}
                            className="flex-shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium text-gray-500 transition-colors hover:bg-gray-100"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : isRenaming ? (
                        <input
                          ref={(el) => {
                            if (el && document.activeElement !== el) {
                              el.focus();
                              el.select();
                            }
                          }}
                          value={renameDraft}
                          aria-label="Rename chat"
                          maxLength={200}
                          onChange={(e) => setRenameDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              skipRenameBlurRef.current = true;
                              commitRename(entry);
                            } else if (e.key === "Escape") {
                              e.preventDefault();
                              cancelRename();
                            }
                          }}
                          onBlur={() => {
                            if (skipRenameBlurRef.current) {
                              skipRenameBlurRef.current = false;
                              return;
                            }
                            commitRename(entry);
                          }}
                          className="mx-2 my-1 w-full min-w-0 rounded-md border border-cyan-300 bg-white px-2 py-1 text-[13px] leading-tight text-gray-700 outline-none focus:border-cyan-500"
                        />
                      ) : (
                        <>
                          <button
                            type="button"
                            title={entry.closed ? `${entry.title} — closed` : entry.title}
                            aria-current={isActive ? "page" : undefined}
                            onClick={() => navigateToSession(`/TseDashboard5/${entry.id}`)}
                            className="flex min-w-0 flex-1 items-center gap-2 px-3 py-2 text-left"
                          >
                            <MessageSquare
                              size={13}
                              className={`flex-shrink-0 ${isActive ? "text-cyan-600" : "text-gray-300 group-hover:text-cyan-600"}`}
                            />
                            <span className="min-w-0 flex-1">
                              <span className={`block truncate text-[13px] leading-tight ${
                                isActive ? "font-medium text-cyan-800" : "text-gray-600 group-hover:text-cyan-800"
                              }`}>
                                {entry.title}
                              </span>
                              <span className={`block truncate text-[10px] leading-tight ${error ? "text-red-500" : "text-gray-400"} ${
                                // motion-safe: so a prefers-reduced-motion reader gets a
                                // static label instead of the pulse. Opacity-only, no
                                // layout change, so the row's height never jitters
                                // between saving / timed-out / confirmed states.
                                isSavingLabel && !error ? "motion-safe:animate-pulse" : ""
                              }`}>
                                {error
                                  || (isSavingLabel
                                    ? "Saving…"
                                    : (
                                      <>
                                        {`${entry.product ? `${entry.product} · ` : ""}${entry.when}`}
                                        {/* Trails the timestamp rather than replacing the
                                            row icon: the icon is how a session is
                                            recognised at a glance, and swapping it made
                                            closed rows read as a different KIND of thing.
                                            A word after the time is the smaller claim. */}
                                        {entry.closed && (
                                          <span className="text-gray-400"> · Closed</span>
                                        )}
                                      </>
                                    ))}
                              </span>
                            </span>
                          </button>
                          {/* Hidden until hover or keyboard focus, and only on
                              rows the API knows about — an optimistic row has
                              no server-side session to rename or delete yet. */}
                          {!entry.pending && (
                            <span className="flex flex-shrink-0 items-center gap-0.5 pr-1.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
                              <button
                                type="button"
                                title="Rename"
                                aria-label={`Rename ${entry.title}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  startRename(entry);
                                }}
                                className="rounded p-1 text-gray-400 transition-colors hover:bg-cyan-100 hover:text-cyan-700"
                              >
                                <Pencil size={12} />
                              </button>
                              <button
                                type="button"
                                title="Delete"
                                aria-label={`Delete ${entry.title}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setRowError(null);
                                  setRenamingId("");
                                  setConfirmingDeleteId(entry.id);
                                }}
                                className="rounded p-1 text-gray-400 transition-colors hover:bg-red-100 hover:text-red-600"
                              >
                                <Trash2 size={12} />
                              </button>
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
                </div>
                {/* The list runs under the footer rather than stopping dead at
                    it, so there is a visible cue that it scrolls. Painted in the
                    sidebar's own colour so it reads as the page, not a panel. */}
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-x-0 bottom-0 h-6"
                  style={{
                    background: `linear-gradient(to top, ${currentSidebarTheme.logoBg}, transparent)`,
                  }}
                />
                </div>
                {/* Outside the scroller, so it stays reachable without scrolling
                    to the end of thirty rows to discover there are more. */}
                {hasMoreChats && (
                  <button
                    type="button"
                    onClick={() => setAllChatsOpen(true)}
                    className="mt-1 flex w-full flex-shrink-0 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-[12px] font-medium text-cyan-700 transition-colors hover:bg-cyan-50"
                  >
                    View all chats
                    <ChevronRight size={12} className="flex-shrink-0" />
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Collapsed, the header is 72px of logo and toggle with no room for a
            version beside them — so it moves to the foot of the rail, directly
            above Logout. Outside the `!hideActions` footer below on purpose:
            the version is not an action, and a rail with its actions hidden
            should still say which build it is. The recents list above owns
            `flex-1`, so this lands at the bottom without a spacer. */}
        {!expanded && (
          <div className="flex justify-center pb-2" title="App version">
            <span className={`text-[10px] font-medium opacity-50 ${currentSidebarTheme.text}`}>
              v{appVersion}
            </span>
          </div>
        )}

        {/* Logout gets its own footer, divided off from everything above it:
            destructive, and not something to sit in the same visual group as
            the things people click all day. */}
        {!hideActions && onLogout && (
          <div
            className={`mt-2 flex flex-col gap-2 border-t pt-3 ${currentSidebarTheme.border} ${
              expanded ? "px-2" : "items-center"
            }`}
          >
            <button
              onClick={onLogout}
              title={!expanded ? "Logout" : undefined}
              className={`flex items-center gap-3 rounded-lg shadow-sm transition-all duration-300 text-red-400 hover:bg-red-50 hover:text-red-600 ${
                expanded ? "px-3 py-2.5 w-full" : "w-10 h-10 justify-center"
              }`}
            >
              <Power size={20} className="flex-shrink-0" />
              {expanded && <span className="text-sm font-medium whitespace-nowrap">Logout</span>}
            </button>
          </div>
        )}

        {/* Portalled to the body from inside, so the sidebar's `overflow-hidden`
            and 240px width do not clip it. `recentSessions` seeds it so it opens
            populated while its own wider fetch is still in the air. Not rendered
            for the batch list — copilot history is irrelevant there, and batch
            has no "view all" of its own. */}
        {!isBatchList && (
          <AllChatsModal
            open={allChatsOpen}
            onClose={() => setAllChatsOpen(false)}
            onSelect={(id) => navigateToSession(`/TseDashboard5/${id}`)}
            activeSessionId={activeSessionId}
            seed={recentSessions}
          />
        )}
      </aside>
    );
  }

  // Default: user sidebar (existing content). Unchanged, and still what the
  // plain user chat gets — only the enterprise copilot opts into the rail with
  // history below it (showRecents).
  if (!entRecents) {
  return (
    <aside className="relative z-[60] flex flex-col justify-between items-center bg-[#F3F3ED] min-w-[72px] w-[72px] py-4 border-r border-[#F3F3ED] h-screen">
      {/* Top: Logo + New Chat */}
      <div className="flex flex-col gap-6 items-center">
        {/* Logo */}
        <div className="mb-2 flex items-center justify-center w-full">
          <img
            src="/A_bgless.png"
            alt="Arta Logo"
            style={{ display: 'block', margin: '0 auto', maxWidth: '40px', height: 'auto' }}
          />
        </div>
      </div>

      <div className="flex-1" />

      {/* Bottom: Account, Upgrade, Install, Power-off Logout Button */}
      <div className="flex flex-col gap-3 items-center mb-2">
        <button
          className="bg-white hover:bg-red-100 rounded-full w-10 h-10 flex items-center justify-center shadow border border-[#EBECE6] transition"
          title="Logout"
          onClick={onLogout}
          style={{ color: '#e53e3e' }}
        >
          <Power size={22} />
        </button>
      </div>
    </aside>
  );
  }

  // ── Enterprise rail: the user sidebar, with chat history ────────────────
  //
  // The same list the copilot's rail draws, from the same state and the same
  // fetch above — only the route a row points at differs (/ent-user/<id>, not
  // /TseDashboard5/<id>, which the role gate would refuse). The agent branch is
  // NOT reused by rendering it here: it carries the agent nav (Escalation,
  // Batch, API Keys) and the dark-theme plumbing, none of which an enterprise
  // user may see. So this is the same mechanism with the agent-only chrome left
  // out, and the agent branch above is untouched.
  const entFilterQuery = recentsFilter.trim().toLowerCase();
  const entMatched = entFilterQuery
    ? recentSessions.filter(
        (s) =>
          String(s.title || "").toLowerCase().includes(entFilterQuery) ||
          String(s.product || "").toLowerCase().includes(entFilterQuery)
      )
    : recentSessions;
  // Re-sorted here as well as at the fetch, for the same reason the agent rail
  // does it: a rename rewrites a row in place and an optimistic row is spliced
  // in at index 0 regardless of its timestamp.
  const entVisible = entMatched.slice().sort(byUpdatedDesc).slice(0, RECENTS_VISIBLE_LIMIT);
  // Suppressed while filtering: "View all" would otherwise read as "all of this
  // subset", and the modal searches the whole history.
  const entHasMore = !entFilterQuery && hasMoreSessions;

  return (
    <aside
      className={`relative z-[60] flex flex-col overflow-hidden ${isEntRail ? entRail.bg : currentSidebarTheme.bg} ${isEntRail ? entRail.border : currentSidebarTheme.border} border-r h-screen py-4 transition-all duration-300 ease-in-out flex-shrink-0`}
      style={{ width: expanded ? 240 : 72, minWidth: expanded ? 240 : 72 }}
    >
      {/* Logo + collapse toggle. Collapsed is the default and is byte-for-byte
          the 72px rail this page had before history existed. */}
      <div className={`mb-4 flex items-center ${expanded ? "justify-between px-4" : "justify-center"}`}>
        <img
          src="/A_bgless.png"
          alt="Arta Logo"
          style={{ display: "block", maxWidth: "36px", height: "auto", flexShrink: 0 }}
        />
        {expanded && (
          <span className={`ml-2 flex-1 text-[11px] font-medium tracking-wide ${isEntRail ? entRail.textMuted : `opacity-60 ${currentSidebarTheme.text}`}`} title="App version">
            v{appVersion}
          </span>
        )}
        <button
          onClick={toggleExpanded}
          className={`w-7 h-7 flex items-center justify-center rounded-full border transition ${isEntRail ? `${entRail.border} ${entRail.iconBtn}` : `${currentSidebarTheme.border} ${currentSidebarTheme.buttonBg} text-gray-400 hover:text-cyan-500`} flex-shrink-0`}
          title={expanded ? "Collapse sidebar" : "Expand sidebar"}
        >
          {expanded ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
        </button>
      </div>

      <div className={`flex min-h-0 flex-1 flex-col ${expanded ? "px-2" : "items-center"}`}>
        {/* New chat. Nothing is created by clicking it: /ent-user is the
            greeting screen, and only the first message turns it into a session
            with a row here (ChatWindow: handleEntNewChat). */}
        <button
          type="button"
          onClick={() => (onNewChat ? onNewChat() : navigate(ENT_BASE_PATH))}
          title={!expanded ? "New chat" : undefined}
          className={`mb-2 flex items-center rounded-lg shadow-sm transition-all duration-300 ${isEntRail ? "border border-white/40 bg-white text-[#2C6CB0] hover:bg-white/90" : "border border-cyan-200 bg-white text-cyan-700 hover:bg-cyan-50 hover:border-cyan-300"} ${
            expanded ? "gap-3 px-3 py-2.5 w-full" : "w-10 h-10 justify-center"
          }`}
        >
          <Plus size={20} className="flex-shrink-0" />
          {expanded && (
            <span className="text-sm font-medium whitespace-nowrap flex-1 text-left">New chat</span>
          )}
        </button>

        <button
          type="button"
          onClick={toggleRecents}
          title={!expanded ? "Recents" : undefined}
          aria-expanded={expanded && recentsOpen}
          className={`flex items-center rounded-lg transition-all duration-300 ${isEntRail ? `${entRail.text} ${entRail.rowHover}` : "text-gray-500 hover:bg-cyan-50 hover:text-cyan-700"} ${
            expanded ? "gap-2 px-3 py-2 w-full min-w-0" : "w-10 h-10 justify-center"
          }`}
        >
          <MessageSquare size={expanded ? 15 : 20} className="relative -top-px flex-shrink-0" />
          {expanded && (
            <>
              <span className="text-[11px] font-semibold uppercase tracking-wider flex-1 min-w-0 truncate text-left">
                Recents
              </span>
              {/* On the header, not over the list: a refresh must not blank out
                  rows the reader is mid-click on. */}
              {recentsLoading && recentSessions.length > 0 && (
                <Loader2 size={11} className="flex-shrink-0 animate-spin text-gray-300" />
              )}
              <ChevronDown
                size={14}
                className={`flex-shrink-0 transition-transform duration-200 ${recentsOpen ? "" : "-rotate-90"}`}
              />
            </>
          )}
        </button>

        {expanded && recentsOpen && (
          <div className="mt-1 flex min-h-0 flex-1 flex-col">
            <div className="relative mb-1 flex-shrink-0 px-1">
              <Search
                size={12}
                aria-hidden="true"
                className={`pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 ${isEntRail ? "text-white/60" : "text-gray-300"}`}
              />
              <input
                type="text"
                value={recentsFilter}
                onChange={(e) => setRecentsFilter(e.target.value)}
                placeholder="Search chats"
                aria-label="Search recent chats"
                className={`w-full rounded-lg border py-1.5 pl-7 pr-6 text-[12px] leading-tight outline-none transition-colors ${isEntRail ? `${entRail.field} focus:border-white/60` : "border-gray-200 bg-white text-gray-600 placeholder-gray-400 focus:border-cyan-300"}`}
              />
              {!!recentsFilter && (
                <button
                  type="button"
                  onClick={() => setRecentsFilter("")}
                  title="Clear search"
                  aria-label="Clear search"
                  className={`absolute right-2.5 top-1/2 -translate-y-1/2 rounded p-0.5 transition-colors ${isEntRail ? "text-white/70 hover:text-white" : "text-gray-400 hover:text-cyan-700"}`}
                >
                  <X size={12} />
                </button>
              )}
            </div>

            <div className="relative min-h-0 flex-1">
              <div className="absolute inset-0 overflow-y-auto d5-recents-scroll pb-3">
                {recentsLoading && recentSessions.length === 0 && (
                  <div className="flex items-center justify-center py-4 text-gray-400">
                    <Loader2 size={16} className="animate-spin" />
                  </div>
                )}
                {!recentsLoading && recentSessions.length === 0 && (
                  <p className={`px-3 py-3 text-[12px] ${isEntRail ? entRail.textMuted : "text-gray-400"}`}>No recent chats</p>
                )}
                {recentSessions.length > 0 && entVisible.length === 0 && (
                  <p className={`px-3 py-3 text-[12px] ${isEntRail ? entRail.textMuted : "text-gray-400"}`}>No matching chats</p>
                )}

                {entVisible.map((entry) => {
                  const isActive = !!activeSessionId && entry.id === activeSessionId;
                  const isRenaming = renamingId === entry.id;
                  const isConfirming = confirmingDeleteId === entry.id;
                  const error = rowError && rowError.id === entry.id ? rowError.message : "";
                  const isSavingLabel = !!entry.pending
                    && !!entry.pendingSince
                    && Date.now() - entry.pendingSince < SAVING_LABEL_TIMEOUT_MS;
                  return (
                    <div
                      key={entry.id}
                      className={`group flex w-full items-center rounded-lg text-left transition-colors ${
                        isActive
                          ? (isEntRail ? entRail.rowActive : "bg-cyan-50")
                          : (isEntRail ? entRail.rowHover : "hover:bg-cyan-100")
                      }`}
                    >
                      {isConfirming ? (
                        <div className="flex w-full items-center gap-1 px-3 py-2">
                          <span className="min-w-0 flex-1 truncate text-[12px] text-gray-600">Delete?</span>
                          <button
                            type="button"
                            onClick={() => deleteSession(entry)}
                            className="flex-shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium text-red-600 transition-colors hover:bg-red-50"
                          >
                            Yes
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmingDeleteId("")}
                            className="flex-shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium text-gray-500 transition-colors hover:bg-gray-100"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : isRenaming ? (
                        <input
                          ref={(el) => {
                            if (el && document.activeElement !== el) {
                              el.focus();
                              el.select();
                            }
                          }}
                          value={renameDraft}
                          aria-label="Rename chat"
                          maxLength={200}
                          onChange={(e) => setRenameDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              skipRenameBlurRef.current = true;
                              commitRename(entry);
                            } else if (e.key === "Escape") {
                              e.preventDefault();
                              cancelRename();
                            }
                          }}
                          onBlur={() => {
                            if (skipRenameBlurRef.current) {
                              skipRenameBlurRef.current = false;
                              return;
                            }
                            commitRename(entry);
                          }}
                          className="mx-2 my-1 w-full min-w-0 rounded-md border border-cyan-300 bg-white px-2 py-1 text-[13px] leading-tight text-gray-700 outline-none focus:border-cyan-500"
                        />
                      ) : (
                        <>
                          <button
                            type="button"
                            title={entry.closed ? `${entry.title} — closed` : entry.title}
                            aria-current={isActive ? "page" : undefined}
                            onClick={() => navigateToSession(sessionPathFor(entry.id))}
                            className="flex min-w-0 flex-1 items-center gap-2 px-3 py-2 text-left"
                          >
                            <MessageSquare
                              size={13}
                              className={`flex-shrink-0 ${isEntRail ? (isActive ? "text-white" : "text-white/70 group-hover:text-white") : (isActive ? "text-cyan-600" : "text-gray-300 group-hover:text-cyan-600")}`}
                            />
                            <span className="min-w-0 flex-1">
                              <span className={`block truncate text-[13px] leading-tight ${
                                isEntRail
                                  ? `${entRail.text} ${isActive ? "font-semibold" : "font-medium"}`
                                  : (isActive ? "font-medium text-cyan-800" : "text-gray-600 group-hover:text-cyan-800")
                              }`}>
                                {entry.title}
                              </span>
                              <span className={`block truncate text-[10px] leading-tight ${error ? (isEntRail ? "text-red-200" : "text-red-500") : (isEntRail ? entRail.textMuted : "text-gray-400")} ${
                                isSavingLabel && !error ? "motion-safe:animate-pulse" : ""
                              }`}>
                                {error
                                  || (isSavingLabel
                                    ? "Saving…"
                                    : (
                                      <>
                                        {`${entry.product ? `${entry.product} · ` : ""}${entry.when}`}
                                        {entry.closed && <span className="text-gray-400"> · Closed</span>}
                                      </>
                                    ))}
                              </span>
                            </span>
                          </button>
                          {/* Only on rows the API knows about — an optimistic
                              row has no server-side session to rename yet. */}
                          {!entry.pending && (
                            <span className="flex flex-shrink-0 items-center gap-0.5 pr-1.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
                              <button
                                type="button"
                                title="Rename"
                                aria-label={`Rename ${entry.title}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  startRename(entry);
                                }}
                                className={`rounded p-1 transition-colors ${isEntRail ? "text-white/75 hover:bg-white/20 hover:text-white" : "text-gray-400 hover:bg-cyan-100 hover:text-cyan-700"}`}
                              >
                                <Pencil size={12} />
                              </button>
                              <button
                                type="button"
                                title="Delete"
                                aria-label={`Delete ${entry.title}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setRowError(null);
                                  setRenamingId("");
                                  setConfirmingDeleteId(entry.id);
                                }}
                                className={`rounded p-1 transition-colors ${isEntRail ? "text-white/75 hover:bg-red-400/30 hover:text-white" : "text-gray-400 hover:bg-red-100 hover:text-red-600"}`}
                              >
                                <Trash2 size={12} />
                              </button>
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
              {/* The list runs under the footer rather than stopping dead at it,
                  so there is a visible cue that it scrolls. */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 bottom-0 h-6"
                style={{ background: `linear-gradient(to top, ${isEntRail ? "#4A90E2" : currentSidebarTheme.logoBg}, transparent)` }}
              />
            </div>

            {entHasMore && (
              <button
                type="button"
                onClick={() => setAllChatsOpen(true)}
                className={`mt-1 flex w-full flex-shrink-0 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-[12px] font-medium transition-colors ${isEntRail ? `${entRail.text} ${entRail.rowHover}` : "text-cyan-700 hover:bg-cyan-50"}`}
              >
                View all chats
                <ChevronRight size={12} className="flex-shrink-0" />
              </button>
            )}
          </div>
        )}
      </div>

      {!expanded && (
        <div className="flex justify-center pb-2" title="App version">
          <span className={`text-[10px] font-medium ${isEntRail ? entRail.textMuted : `opacity-50 ${currentSidebarTheme.text}`}`}>v{appVersion}</span>
        </div>
      )}

      <div className={`mt-2 flex flex-col gap-2 border-t pt-3 ${isEntRail ? entRail.border : currentSidebarTheme.border} ${expanded ? "px-2" : "items-center"}`}>
        <button
          onClick={onLogout}
          title={!expanded ? "Logout" : undefined}
          className={`flex items-center gap-3 rounded-lg shadow-sm transition-all duration-300 ${isEntRail ? "text-white hover:bg-red-400/30" : "text-red-400 hover:bg-red-50 hover:text-red-600"} ${
            expanded ? "px-3 py-2.5 w-full" : "w-10 h-10 justify-center"
          }`}
        >
          <Power size={20} className="flex-shrink-0" />
          {expanded && <span className="text-sm font-medium whitespace-nowrap">Logout</span>}
        </button>
      </div>

      {/* Prop-driven and portalled to the body, so the same component serves
          both rails with no change of its own. */}
      <AllChatsModal
        open={allChatsOpen}
        onClose={() => setAllChatsOpen(false)}
        onSelect={(id) => navigateToSession(sessionPathFor(id))}
        activeSessionId={activeSessionId}
        seed={recentSessions}
        surface={surfaceScope}
      />
    </aside>
  );
};

export default SideBar;
